import { queryOptions } from '@tanstack/react-query'
import { apiFetch } from '@shared/api/http'
import { toQueryString } from '@shared/api/query'
import { analyticsPeriod } from '@shared/lib/analytics-period'
import {
  AwardRequestResponseSchema,
  AwardsListSchema,
  AwardsPerformanceSchema,
  DashboardLiveSchema,
  DashboardOverviewSchema,
} from './dashboard.contract'
import type {
  AwardRequestResponse,
  AwardsList,
  AwardsPerformance,
  DashboardLive,
  DashboardOverview,
  DashboardRange,
  DashboardView,
} from './dashboard.contract'

export interface DashboardOverviewFilters {
  range: DashboardRange
  from?: string
  to?: string
  productId?: string
  view?: DashboardView
}

export interface DashboardLiveFilters {
  range: DashboardRange
  from?: string
  to?: string
  productId?: string
}

export type DashboardLiveResult = DashboardLive & { periodLabel: string }

export const dashboardKeys = {
  overview: (filters: DashboardOverviewFilters) => ['dashboard', 'overview', filters] as const,
  live: (filters: DashboardLiveFilters) => ['dashboard', 'live', filters] as const,
  awardsPerformance: () => ['dashboard', 'awards', 'performance'] as const,
  awardsList: () => ['dashboard', 'awards', 'list'] as const,
}

function dashboardQueryString(filters: DashboardOverviewFilters): string {
  return toQueryString({
    range: filters.range,
    from: filters.from,
    to: filters.to,
    productId: filters.productId,
    view: filters.view,
  })
}

export function dashboardOverviewQuery(filters: DashboardOverviewFilters) {
  return queryOptions({
    queryKey: dashboardKeys.overview(filters),
    queryFn: async (): Promise<DashboardOverview> => {
      const query = dashboardQueryString(filters)
      const data = await apiFetch<unknown>(`/dashboard/overview${query}`)
      return DashboardOverviewSchema.parse(data)
    },
  })
}

export function dashboardLiveQuery(filters: DashboardLiveFilters) {
  return queryOptions({
    queryKey: dashboardKeys.live(filters),
    queryFn: async (): Promise<DashboardLiveResult> => {
      const period = analyticsPeriod({
        preset: filters.range,
        from: filters.range === 'custom' ? (filters.from ?? null) : null,
        to: filters.range === 'custom' ? (filters.to ?? null) : null,
      })
      const query = dashboardQueryString({
        range: filters.range,
        from: period.from,
        to: period.to,
        productId: filters.productId,
        view: 'all',
      })
      const data = await apiFetch<unknown>(`/dashboard/live${query}`)
      return { ...DashboardLiveSchema.parse(data), periodLabel: period.label }
    },
  })
}

export function awardsPerformanceQuery() {
  return queryOptions({
    queryKey: dashboardKeys.awardsPerformance(),
    queryFn: async (): Promise<AwardsPerformance> => {
      return AwardsPerformanceSchema.parse(await apiFetch<unknown>('/premiacao/desempenho'))
    },
  })
}

export function awardsListQuery() {
  return queryOptions({
    queryKey: dashboardKeys.awardsList(),
    queryFn: async (): Promise<AwardsList> => {
      return AwardsListSchema.parse(await apiFetch<unknown>('/premiacao/minhas'))
    },
  })
}

export interface RequestAwardInput {
  metaId: string
  endereco: Record<string, unknown>
}

export async function requestAward(input: RequestAwardInput): Promise<AwardRequestResponse> {
  const data = await apiFetch<unknown>('/premiacao/solicitar', {
    method: 'POST',
    body: input,
  })
  return AwardRequestResponseSchema.parse(data)
}
